import { icon } from '../lib/icons';
import { workerHttpUrl } from '../lib/worker-config';
import { siteHeaderHtml, bindSiteNav } from '../lib/page-shell';
import type { RouteHandler } from '../lib/router';

/**
 * The platform front door (owner brief 2026-08-18: "Timing Games = Platform,
 * Stop the Clock / Stack Tower = individual games"). Deliberately not
 * duel-centric any more — "Play a Duel" is gone; the primary actions are
 * Daily Challenge (jump straight into today's target) and View Games (the
 * game library, play-menu.ts), with the two game preview cards below making
 * it immediately obvious this is a collection, not one game with a menu.
 */
export const renderHome: RouteHandler = (container, navigate) => {
  container.innerHTML = `
    <div class="app-shell">
      ${siteHeaderHtml(
        'home',
        `<span class="network-pill" id="network-pill" data-state="checking"><i aria-hidden="true"></i> Connecting…</span>`,
      )}

      <main id="main-content" class="main-content">
        <section class="hero" aria-labelledby="hero-title">
          <div class="hero-copy">
            <div class="brand-kicker">
              <span class="brand-kicker__bar" aria-hidden="true"></span>
              <span class="brand-kicker__text"><strong>Timing Games</strong><small>Competitive Timing Games</small></span>
            </div>
            <h1 id="hero-title">YOUR SENSE<br />OF <em>TIME.</em></h1>
            <p class="hero-intro">
              Test your internal clock across a collection of competitive timing games. Sharpen your reactions, master your precision, climb the rankings and improve your sense of time.
            </p>

            <div class="hero-actions">
              <button class="button button--primary" type="button" data-nav="daily-challenge">
                ${icon('target')} Daily Challenge
              </button>
              <button class="button button--primary" type="button" data-nav="play">
                ${icon('grid')} View Games ${icon('arrow')}
              </button>
            </div>
            <button class="hero-secondary-link" type="button" data-nav="challenge-10s">
              10 Second Challenge ${icon('arrow')}
            </button>
          </div>
        </section>

        <section class="game-previews" aria-label="Games">
          <h2 class="section-kicker">Games</h2>
          <div class="game-preview-grid">
            <article class="game-preview-card">
              <header><span>Stop the Clock</span><small>Timing duel</small></header>
              <div class="game-preview-card__visual game-preview-card__visual--clock">
                <div class="mini-timer">
                  <span class="mini-timer__label">Target locked</span>
                  <span class="mini-timer__readout"><b>13</b>.<b>7</b><small>SEC</small></span>
                </div>
              </div>
              <button class="button button--secondary" type="button" data-nav="play/stop-the-clock">
                Play Now ${icon('arrow')}
              </button>
            </article>

            <article class="game-preview-card">
              <header><span>Stack Tower</span><small>Stacking climb</small></header>
              <div class="game-preview-card__visual game-preview-card__visual--tower" aria-hidden="true">
                <div class="mini-tower">
                  <i style="--w:58%"></i>
                  <i style="--w:74%"></i>
                  <i style="--w:66%"></i>
                  <i style="--w:88%"></i>
                  <i style="--w:100%"></i>
                </div>
              </div>
              <button class="button button--secondary" type="button" data-nav="play/stack-tower">
                Play Now ${icon('arrow')}
              </button>
            </article>
          </div>
        </section>

        <section class="platform-blurb">
          <h2 class="section-kicker">Competitive Timing Games</h2>
          <p>
            Compete, improve and make it yours. Climb the leaderboards, unlock achievements, collect call
            signs and banners, challenge friends and customise your profile.
          </p>
          <ul class="platform-blurb__tags" aria-hidden="true">
            <li>Practice</li>
            <li>Leaderboards</li>
            <li>Achievements</li>
            <li>Call signs</li>
            <li>Banners</li>
            <li>Customisation</li>
            <li>Friends &amp; challenges</li>
          </ul>
        </section>
      </main>

      <footer class="site-footer">
        <span>TG // PRE-SEASON</span>
        <span>BUILDING THE FAIR PLAYGROUND FOR TIME</span>
      </footer>
    </div>
  `;

  // Wires every [data-nav] button in the whole container — header, hero,
  // preview cards, everything — in one pass; see bindSiteNav's own comment.
  bindSiteNav(container, navigate);

  // A real connectivity check against the actual Worker this build points
  // at, not a hardcoded "Local play ready" string (owner feedback,
  // 2026-08-11: that copy read as a leftover dev-mode indicator on the
  // live deployed site, since it never changed regardless of environment).
  // /api/catalog is public, no auth required, cheap.
  const pill = container.querySelector<HTMLSpanElement>('#network-pill');
  if (pill) {
    fetch(`${workerHttpUrl()}/api/catalog`)
      .then((res) => {
        pill.dataset.state = res.ok ? 'online' : 'offline';
        pill.lastChild!.textContent = res.ok ? ' Online' : ' Offline';
      })
      .catch(() => {
        pill.dataset.state = 'offline';
        pill.lastChild!.textContent = ' Offline';
      });
  }
};
